import { useEffect, useRef, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import FormInput from "../../Form/FormInput";
import FormPhoneInput from "../../Form/FormPhoneInput";
import FormRadioGroup from "../../Form/FormRadioGroup";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  clearBusinessPartner,
  clearBusinessPartnerError,
  requestCreateBusinessPartner,
  requestUpdateBusinessPartner,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import { BUSINESS_TYPES_OPTIONS } from "../../../../Constants/business";
import { cancelAPIRequest } from "../../../../Utils";

const ContactModal = ({ open, onClose, partner }) => {
  const { t } = useTranslation();

  const [businessType, setBusinessType] = useState(null);
  const [name, setName] = useState("");
  const [npwp, setNpwp] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [errors, setErrors] = useState({});

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user, getUserLoading: userLoading } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    request,
    newPartner,
    newPartnerLoading,
    newPartnerError,
    partnerID: editPartner,
    updatePartnerLoading: editPartnerLoading,
    updatePartnerError: editPartnerError,
  } = useBusinessTransactionState();

  const isEdit = Boolean(partner);
  const submitDisabled = userLoading;

  // Business type is picked from a radio group, which SlideOver's own typing
  // detection cannot see — so the form states what is worth confirming over.
  const dirty =
    isEdit ||
    Boolean(businessType || name || npwp || email || phone || address);
  const submitLoading = newPartnerLoading || editPartnerLoading;
  const cleanedPhone = phone?.replace(/\s/g, "");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Clean up on unmount only. With `request` as a dependency this cleanup also
  // ran when a failed save reset `request` to null, wiping the error before it
  // was shown, and cancelled whatever request the page had in flight.
  const requestRef = useRef(request);
  requestRef.current = request;
  useEffect(() => {
    return () => {
      cancelAPIRequest(requestRef.current);
      clearBusinessPartner(dispatch);
      clearBusinessPartnerError(dispatch);
    };
  }, [dispatch]);

  // Populate form when editing
  useEffect(() => {
    if (partner) {
      setName(partner.name);
      setNpwp(partner.taxNumber || "");
      setEmail(partner.email || "");
      setPhone(partner.phone || "");
      setBusinessType(
        BUSINESS_TYPES_OPTIONS.find((type) => type.value === partner.type),
      );
      setAddress(partner.address || "");
    }
  }, [partner]);

  //  Handle create success
  useEffect(() => {
    if (newPartner) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-product-${newPartner}`,
        type: "success",
        title: "common.success",
        body: "contacts.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, newPartner, onClose]);

  //  Handle update success
  useEffect(() => {
    if (editPartner) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `edit-product-${editPartner}`,
        type: "success",
        title: "common.success",
        body: "contacts.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, editPartner, onClose]);

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const validate = () => {
    const newErrors = {};

    if (!name) {
      newErrors.name = "contacts.errors.nameRequired";
    }

    if (!businessType?.value) {
      newErrors.businessType = "contacts.errors.typeRequired";
    }

    if (email) {
      const isEmailValid = emailRegex.test(email);
      if (!isEmailValid) {
        newErrors.email = "contacts.errors.email";
      }
    }

    if (phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (!isPhoneValid) {
        newErrors.phone = "contacts.errors.phone";
      }
    }

    if (npwp) {
      const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;
      if (!isNpwpValid) {
        newErrors.npwp = "contacts.errors.npwp";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    savePartner();
  };

  const savePartner = () => {
    const input = {
      id: isEdit ? partner.id : undefined,
      businessID: user.mainBusiness.id,
      name,
      email,
      type: businessType.value,
      phone: cleanedPhone,
      taxNumber: npwp,
      address,
    };

    if (isEdit) {
      requestUpdateBusinessPartner(dispatch, { input });
    } else {
      requestCreateBusinessPartner(dispatch, { input });
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={isEdit ? "contacts.modal.title.edit" : "contacts.modal.title.add"}
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="contact-form"
            variant="primary"
            disabled={writeBlocked || submitDisabled}
            loading={submitLoading}
          >
            {isEdit ? t("common.edit") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form id="contact-form" className="space-y-4" onSubmit={handleSubmit}>
        <WriteBlockGate>
        <FormError error={newPartnerError || editPartnerError} />
        <Fieldset className="space-y-4">
          <Field>
            <FormInput
              required
              label="contacts.fields.name"
              id="name"
              name="name"
              value={name}
              onChange={handleFieldChange(setName, "name")}
              placeholder={t("contacts.placeholders.name")}
              error={errors.name}
            />
          </Field>
          <Field>
            <FormRadioGroup
              required
              label="contacts.fields.type"
              name="business-type"
              options={BUSINESS_TYPES_OPTIONS}
              value={businessType}
              onChange={handleFieldChange(setBusinessType, "businessType")}
              error={errors.businessType}
            />
          </Field>
          <Field>
            <FormInput
              label="contacts.fields.npwp"
              id="npwp"
              name="npwp"
              value={npwp}
              onChange={handleFieldChange(setNpwp, "npwp")}
              placeholder={t("contacts.placeholders.npwp")}
              hint={t("common.optional")}
              error={errors.npwp}
              allowLeadingZeros
              numberFormat
              isAllowed={({ value }) => value.length <= 16}
            />
          </Field>
          <Field>
            <FormInput
              label="contacts.fields.email"
              id="email"
              name="email"
              value={email}
              onChange={handleFieldChange(setEmail, "email")}
              placeholder={t("contacts.placeholders.email")}
              hint={t("common.optional")}
              error={errors.email}
              type="email"
            />
          </Field>
          <Field>
            <FormPhoneInput
              label="contacts.fields.phone"
              id="phone"
              name="phone"
              value={phone}
              onChange={handleFieldChange(setPhone, "phone")}
              placeholder={t("contacts.placeholders.phone")}
              hint={t("common.optional")}
              error={errors.phone}
            />
          </Field>
          <Field>
            <FormInput
              label="contacts.fields.address"
              id="address"
              name="address"
              value={address}
              onChange={handleFieldChange(setAddress, "address")}
              placeholder={t("contacts.placeholders.address")}
              hint={t("common.optional")}
            />
          </Field>
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default ContactModal;
